<template>
  <label
    class="ui-checkbox"
    :class="[`ui-checkbox--${size}`, { 'ui-checkbox--disabled': disabled, 'ui-checkbox--error': Boolean(error) }]"
  >
    <input
      :id="id"
      ref="element"
      data-test-id="ui-checkbox"
      v-bind="$attrs"
      :checked="modelValue"
      :disabled="disabled"
      :name="name"
      :required="required"
      type="checkbox"
      :value="value"
      @change="change"
    >
    <span aria-hidden="true" class="ui-checkbox__box">
      <svg viewBox="0 0 18 18">
        <rect v-if="indeterminate" height="3" width="15" x="1.5" y="7.5" />
        <polyline v-else points="1 9 7 14 15 4" />
      </svg>
    </span>
    <span v-if="label || description || error || $slots.default" class="ui-checkbox__content">
      <span class="ui-checkbox__label"><slot>{{ label }}</slot></span>
      <span v-if="description" class="ui-checkbox__description">{{ description }}</span>
      <span v-if="error" class="ui-checkbox__error">{{ error }}</span>
    </span>
  </label>
</template>
<script setup lang="ts">
  import { ref, watchEffect } from 'vue'

  defineOptions({ inheritAttrs: false, name: 'UiCheckbox' })
  const props = withDefaults(
    defineProps<{
      description?: string
      disabled?: boolean
      error?: string
      id?: string
      indeterminate?: boolean
      label?: string
      modelValue?: boolean
      name?: string
      required?: boolean
      size?: 'md' | 'sm'
      value?: number | string
    }>(),
    { modelValue: false, size: 'md' },
  )
  const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()
  const element = ref<HTMLInputElement>()
  watchEffect(() => {
    if (element.value) element.value.indeterminate = props.indeterminate
  })
  const change = (event: Event) => {
    emit('update:modelValue', (event.target as HTMLInputElement).checked)
  }
  defineExpose({ element })
</script>
<style scoped lang="scss">
@use '@repo/ui/typography.scss' as typography;

/* Checked, disabled, error and focus states intentionally override hover. */
/* stylelint-disable no-descending-specificity */

.ui-checkbox {
 align-items: flex-start;
 color: var(--color-text-text-secondary-700);
 cursor: pointer;
 display: inline-flex;
 gap: 8px;
 position: relative;

}

input {
 height: 1px;
 opacity: 0;
 position: absolute;
 width: 1px;

}

.ui-checkbox__box {
 align-items: center;
 border: 2px solid var(--color-border-border-primary);
 border-radius: var(--radius-xs);
 color: transparent;
 display: inline-flex;
 flex: none;
 justify-content: center;
 height: 18px;
 transition: all 200ms;
 width: 18px;

}

.ui-checkbox__box svg {
 fill: none;
 height: 16px;
 stroke: var(--color-base-white);
 stroke-dasharray: 22px;
 stroke-dashoffset: 66;
 stroke-width: 3px;
 transition: all 200ms;
 width: 16px;
}

.ui-checkbox--sm .ui-checkbox__box {
 top: 1px;

}

.ui-checkbox--md .ui-checkbox__box {
 top: 3px;

}

input:focus-visible + .ui-checkbox__box {
 outline: 2px solid var(--color-effects-focus-rings-focus-ring) !important;
 outline-offset: 2px;

}


input:checked + .ui-checkbox__box,
input:indeterminate + .ui-checkbox__box {
 background: var(--color-background-bg-brand-solid);
 border-color: var(--color-background-bg-brand-solid);

}

input:checked + .ui-checkbox__box svg,
input:indeterminate + .ui-checkbox__box svg {
 stroke-dashoffset: 44;
}

input:indeterminate + .ui-checkbox__box svg {
 fill: var(--color-base-white);
 stroke: none;
}

input:active:not(:disabled) + .ui-checkbox__box {
 border-color: var(--color-border-border-primary);

}

input:checked:active:not(:disabled) + .ui-checkbox__box,
input:indeterminate:active:not(:disabled) + .ui-checkbox__box {
 background: var(--color-border-border-primary);
}

input:disabled + .ui-checkbox__box {
 border-color: var(--color-border-border-primary);

}

.ui-checkbox__content {
 display: flex;
 flex-direction: column;

}

.ui-checkbox__label {
 @include typography.font-style('text-md', 'medium');

}

.ui-checkbox--sm .ui-checkbox__label {
 @include typography.font-style('text-sm', 'medium');
}

.ui-checkbox__description,
.ui-checkbox__error {
 @include typography.font-style('text-sm', 'regular');

 color: var(--color-text-text-tertiary-600);

}

.ui-checkbox__error {
 color: var(--color-error-600);

}

.ui-checkbox--disabled {
 cursor: not-allowed;
 opacity: 0.5;

}

</style>
